import React from "react";
import { motion } from "framer-motion";

// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { alpha, useTheme } from "@mui/material/styles";

// ICONS
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";

// Animation variants
const itemVariants = {
  hidden: { y: 10, opacity: 0 },
  show: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

const CategoryRow = ({ index, category, deleteAction, editAction }) => {
  const theme = useTheme();

  const getTextColor = (hexColor) => {
    if (!hexColor) {
      return "#ffffff";
    }

    const hex = hexColor.replace("#", "");

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    const brightness = (r * 299 + g * 587 + b * 114) / 1000;

    return brightness > 128 ? "#000000" : "#ffffff";
  };

  const handleRowClick = (event) => {
    const isActionButton = event.target.closest("button");

    if (isActionButton) {
      return;
    }

    editAction();
  };

  const textColor = getTextColor(category.color_hex);

  return (
    <Paper
      elevation={0}
      component={motion.div}
      variants={itemVariants}
      onClick={handleRowClick}
      whileHover={{
        y: -2,
        boxShadow: theme.shadows[2],
        backgroundColor: alpha(theme.palette.background.paper, 0.9),
      }}
      sx={{
        mb: 2,
        borderRadius: 2,
        backgroundColor: theme.palette.grey[50],
        overflow: "hidden",
        transition: "all 0.2s ease-in-out",
        position: "relative",
        cursor: "pointer",
      }}
    >
      <Box sx={{ pl: 3, pr: 2, py: 1.5 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ flex: 1 }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor:
                  category.color_hex || theme.palette.primary.main,
                color: textColor,
                borderRadius: 1.5,
                p: 0.75,
                minWidth: 36,
                height: 36,
              }}
            >
              <LocalOfferIcon fontSize="small" />
            </Box>

            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body1" fontWeight={600}>
                {category.title}
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  backgroundColor: alpha(theme.palette.grey[500], 0.1),
                  color: theme.palette.text.secondary,
                }}
              >
                #{index}
              </Typography>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={0.5}>
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
              <Tooltip title="Editar Categoria">
                <IconButton
                  size="small"
                  onClick={editAction}
                  sx={{
                    color: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    p: 0.75,
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                    },
                  }}
                >
                  <EditIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </motion.div>

            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
              <Tooltip title="Remover Categoria">
                <IconButton
                  size="small"
                  onClick={deleteAction}
                  sx={{
                    color: theme.palette.error.main,
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                    p: 0.75,
                    "&:hover": {
                      bgcolor: alpha(theme.palette.error.main, 0.2),
                    },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </motion.div>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
};

export const CategoryList = ({ children }) => {
  return (
    <motion.div
      initial="hidden"
      animate="show"
      variants={{
        hidden: { opacity: 0 },
        show: {
          opacity: 1,
          transition: {
            staggerChildren: 0.1,
          },
        },
      }}
    >
      {children}
    </motion.div>
  );
};

export default CategoryRow;
